import * as React from 'react';
import {
  Home,
  Shield,
  ClipboardList,
  Users,
  Settings,
  FileText,
  Bell,
  LogOut,
  ChevronLeft,
  ChevronRight,
  BarChart3,
  HelpCircle,
  Building2,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Sidebar } from '@components/base/navigation';
import { Button } from '@components/base/actions';
import { Icon, Text } from '@components/base/display';
import { VStack, HStack } from '@components/base/layout';
import { ShowcasePreviewSection } from '../../components/shared/ShowcasePreviewSection';
import { ShowcaseDemoSection } from '../../components/shared/ShowcaseDemoSection';
import type { ShowcaseDefinition, ShowcaseProps } from '../types';

// Type for sidebar item configuration
interface SidebarItemConfig {
  readonly icon: LucideIcon;
  readonly label: string;
  readonly active?: boolean;
  readonly disabled?: boolean;
}

// Type for a sidebar section with its items
interface SidebarSectionConfig {
  readonly title: string;
  readonly items: readonly SidebarItemConfig[];
}

// Simple logo component for sidebar showcases
function Logo() {
  return (
    <div className="w-8 h-8 bg-[#334E68] rounded-lg flex items-center justify-center">
      <Icon icon={Shield} size="sm" className="text-white" />
    </div>
  );
}

// Renders a list of sidebar items from config
function SidebarItems({ items }: Readonly<{ items: readonly SidebarItemConfig[] }>) {
  return (
    <>
      {items.map((item) => (
        <Sidebar.Item
          key={item.label}
          icon={<Icon icon={item.icon} size="sm" />}
          label={item.label}
          href="#"
          active={item.active}
          disabled={item.disabled}
        />
      ))}
    </>
  );
}

// Renders multiple sidebar sections from config
function SidebarSections({ sections }: Readonly<{ sections: readonly SidebarSectionConfig[] }>) {
  return (
    <>
      {sections.map((section) => (
        <Sidebar.Section key={section.title} title={section.title}>
          <SidebarItems items={section.items} />
        </Sidebar.Section>
      ))}
    </>
  );
}

// Common navigation items used across multiple sidebar demos
const coreNavItems: readonly SidebarItemConfig[] = [
  { icon: Home, label: 'Dashboard', active: true },
  { icon: Shield, label: 'Policies' },
  { icon: ClipboardList, label: 'Claims' },
];

function CoreNavSection({ title = 'Main', extraItems }: Readonly<{ title?: string; extraItems?: readonly SidebarItemConfig[] }>) {
  const allItems = extraItems ? [...coreNavItems, ...extraItems] : coreNavItems;
  return (
    <Sidebar.Section title={title}>
      <SidebarItems items={allItems} />
    </Sidebar.Section>
  );
}

// Data arrays for each demo section
const basicSidebarItems: readonly SidebarItemConfig[] = [
  { icon: Home, label: 'Home' },
  { icon: Shield, label: 'My Policies' },
  { icon: ClipboardList, label: 'My Claims' },
  { icon: FileText, label: 'Documents' },
];

const withSectionsSections: readonly SidebarSectionConfig[] = [
  {
    title: 'Overview',
    items: [
      { icon: Home, label: 'Dashboard', active: true },
      { icon: BarChart3, label: 'Analytics' },
    ],
  },
  {
    title: 'Management',
    items: [
      { icon: Shield, label: 'Policies' },
      { icon: ClipboardList, label: 'Claims' },
      { icon: Users, label: 'Customers' },
    ],
  },
  {
    title: 'Administration',
    items: [
      { icon: Building2, label: 'Agency' },
      { icon: Settings, label: 'Settings' },
    ],
  },
];

const footerItems: readonly SidebarItemConfig[] = [
  { icon: Bell, label: 'Notifications' },
  { icon: HelpCircle, label: 'Help & Support' },
  { icon: LogOut, label: 'Sign Out' },
];

const activeStatesBaseItems: readonly SidebarItemConfig[] = [
  { icon: Home, label: 'Dashboard' },
  { icon: Shield, label: 'Policies' },
  { icon: ClipboardList, label: 'Claims' },
];

const disabledDemoItems: readonly SidebarItemConfig[] = [
  { icon: Home, label: 'Dashboard', active: true },
  { icon: Shield, label: 'Policies' },
  { icon: ClipboardList, label: 'Claims', disabled: true },
  { icon: BarChart3, label: 'Reports', disabled: true },
  { icon: Users, label: 'Customers' },
];

function SidebarShowcaseComponent({ playgroundProps, isDarkMode: _isDarkMode }: Readonly<ShowcaseProps>) {
  const [isCollapsed, setIsCollapsed] = React.useState(playgroundProps.collapsed as boolean);

  // Sync with playground props
  React.useEffect(() => {
    setIsCollapsed(playgroundProps.collapsed as boolean);
  }, [playgroundProps.collapsed]);

  return (
    <VStack gap={6}>
      {/* Interactive Preview */}
      <ShowcasePreviewSection componentName="Use the Props Playground to toggle the collapsed state">
          <div className="h-[400px] border border-[#E8E6E3] rounded-lg overflow-hidden">
            <Sidebar collapsed={isCollapsed} onCollapsedChange={setIsCollapsed}>
              <Sidebar.Header logo={<Logo />} title="Insurance Portal" />
              <CoreNavSection extraItems={[{ icon: Users, label: 'Customers' }]} />
              <Sidebar.Footer>
                <Sidebar.Item
                  icon={<Icon icon={Settings} size="sm" />}
                  label="Settings"
                  href="#"
                />
              </Sidebar.Footer>
            </Sidebar>
          </div>
      </ShowcasePreviewSection>

      {/* Basic Sidebar */}
      <ShowcaseDemoSection
        title="Basic Sidebar"
        description="A simple sidebar with navigation items"
       
      >
          <div className="h-[350px] border border-[#E8E6E3] rounded-lg overflow-hidden">
            <Sidebar>
              <Sidebar.Header logo={<Logo />} title="My Insurance" />
              <Sidebar.Section>
                <SidebarItems items={basicSidebarItems} />
              </Sidebar.Section>
            </Sidebar>
          </div>
      </ShowcaseDemoSection>

      {/* Collapsible Sidebar */}
      <ShowcaseDemoSection
        title="Collapsible Sidebar"
        description="Sidebar can be collapsed to save space, showing only icons with tooltips"
       
      >
          <VStack gap={4}>
            <HStack gap={2}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setIsCollapsed(!isCollapsed)}
                leftIcon={<Icon icon={isCollapsed ? ChevronRight : ChevronLeft} size="sm" />}
              >
                {isCollapsed ? 'Expand' : 'Collapse'} Sidebar
              </Button>
              <Text size="sm" color="secondary">
                {isCollapsed ? 'Collapsed - hover icons for tooltips' : 'Expanded - full labels visible'}
              </Text>
            </HStack>
            <div className="h-[350px] border border-[#E8E6E3] rounded-lg overflow-hidden flex">
              <Sidebar collapsed={isCollapsed}>
                <Sidebar.Header logo={<Logo />} title="Insurance Portal" />
                <CoreNavSection title="Navigation" />
                <Sidebar.Footer>
                  <Sidebar.Item
                    icon={<Icon icon={Settings} size="sm" />}
                    label="Settings"
                    href="#"
                  />
                </Sidebar.Footer>
              </Sidebar>
              <div className="flex-1 p-4 bg-white">
                <Text color="secondary">Main content area</Text>
              </div>
            </div>
          </VStack>
      </ShowcaseDemoSection>

      {/* With Sections */}
      <ShowcaseDemoSection
        title="With Sections"
        description="Organize navigation items into labeled sections"
       
      >
          <div className="h-[450px] border border-[#E8E6E3] rounded-lg overflow-hidden">
            <Sidebar>
              <Sidebar.Header logo={<Logo />} title="Agent Portal" />
              <SidebarSections sections={withSectionsSections} />
            </Sidebar>
          </div>
      </ShowcaseDemoSection>

      {/* With Footer */}
      <ShowcaseDemoSection
        title="With Footer"
        description="Footer section stays at the bottom for user actions and settings"
       
      >
          <div className="h-[400px] border border-[#E8E6E3] rounded-lg overflow-hidden">
            <Sidebar>
              <Sidebar.Header logo={<Logo />} title="Insurance App" />
              <CoreNavSection />
              <Sidebar.Footer>
                <SidebarItems items={footerItems} />
              </Sidebar.Footer>
            </Sidebar>
          </div>
      </ShowcaseDemoSection>

      {/* Active States */}
      <ShowcaseDemoSection
        title="Active States"
        description="Active items are highlighted to show current location"
       
      >
          <HStack gap={4} wrap="wrap">
            {activeStatesBaseItems.map((activeItem) => (
              <div key={activeItem.label} className="h-[300px] w-[240px] border border-[#E8E6E3] rounded-lg overflow-hidden">
                <Sidebar>
                  <Sidebar.Header logo={<Logo />} title="Portal" />
                  <Sidebar.Section>
                    <SidebarItems
                      items={activeStatesBaseItems.map((item) => ({
                        ...item,
                        active: item.label === activeItem.label,
                      }))}
                    />
                  </Sidebar.Section>
                </Sidebar>
              </div>
            ))}
          </HStack>
      </ShowcaseDemoSection>

      {/* Disabled Items */}
      <ShowcaseDemoSection
        title="Disabled Items"
        description="Navigation items can be disabled to prevent access"
       
      >
          <div className="h-[350px] border border-[#E8E6E3] rounded-lg overflow-hidden">
            <Sidebar>
              <Sidebar.Header logo={<Logo />} title="Insurance Portal" />
              <Sidebar.Section title="Navigation">
                <SidebarItems items={disabledDemoItems} />
              </Sidebar.Section>
            </Sidebar>
          </div>
      </ShowcaseDemoSection>
    </VStack>
  );
}

export const SidebarShowcase: ShowcaseDefinition = {
  id: 'sidebar',
  name: 'Sidebar',
  category: 'navigation',
  description: 'Vertical navigation panel with sections and items',
  propsDefinition: [
    {
      name: 'collapsed',
      type: 'boolean',
      defaultValue: false,
      description: 'Whether the sidebar is collapsed to icon-only mode',
    },
  ],
  accessibility: {
    ariaAttributes: [
      'nav element with aria-label="Main navigation"',
      'aria-current="page" on active item',
      'aria-expanded for expandable items',
      'aria-disabled for disabled items',
    ],
    keyboardNav: [
      'Tab: Navigate between sidebar items',
      'Enter/Space: Activate item or toggle expansion',
      'Focus moves to nested items when expanded',
    ],
    notes: [
      'Collapsed mode shows tooltips on hover',
      'Section labels are screen-reader only when collapsed',
      'Uses semantic nav, ul, and li structure',
      'Active state includes visual indicator (left border)',
    ],
  },
  component: SidebarShowcaseComponent,
};
